import { useMe } from '../providers/Me';
import { useQuery, gql, useMutation } from '@apollo/client';
import { Loading, MyTextField } from './Widgets';
import { useState } from 'react';
import LaunchIcon from '@mui/icons-material/Launch';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  Link,
  FormGroup,
  FormControlLabel,
  Alert,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Settings } from '../Settings';

const CreateJsonReportComponent = ({ ssid, onChange }) => {
  const [excludeGermline, setExcludeGermline] = useState(false);
  const [skipValidation, setSkipValidation] = useState(true);
  const [skippedSamples, setSkippedSamples] = useState(['ctdna']);
  const [usingTreatmentSetting, setUsingTreatmentSetting] = useState('');

  const [createJsonReport, { data, loading, error }] = useMutation(gql`
    mutation CreateJsonReportMutation(
      $input: GenerateJsonFromOncodnaFilesMutationInputType!
    ) {
      generateJsonFromOncodnaFiles(input: $input) {
        runtimeSeconds
      }
    }
  `);

  const toggleSkippedSample = (sample) => {
    if (skippedSamples.includes(sample)) {
      setSkippedSamples(skippedSamples.filter((s) => s !== sample));
    } else {
      setSkippedSamples([...skippedSamples, sample]);
    }
  };

  const input = {
    ssid,
    excludeGermline,
    skipValidation,
    // convert to csv:
    skippedSamples: skippedSamples.join(','),
    usingTreatmentSetting,
  };

  return (
    <>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={excludeGermline}
              onChange={(e) => setExcludeGermline(e.target.checked)}
            />
          }
          label="Exclude Germline"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={skipValidation}
              onChange={(e) => setSkipValidation(e.target.checked)}
            />
          }
          label="Skip Validation"
        />
        {['primary', 'meta', 'blood', 'ctdna'].map((sample) => (
          <FormControlLabel
            key={sample}
            control={
              <Checkbox
                checked={skippedSamples.includes(sample)}
                onChange={() => toggleSkippedSample(sample)}
              />
            }
            label={`Skip ${sample} sample`}
          />
        ))}

        <FormControl fullWidth>
          <InputLabel id="treatment-setting-label-id">
            Primary treatment setting
          </InputLabel>
          <Select
            labelId="treatment-setting-label-id"
            value={usingTreatmentSetting}
            label="Using primary treatment setting"
            onChange={(e) => setUsingTreatmentSetting(e.target.value)}
          >
            <MenuItem value="">Default</MenuItem>
            <MenuItem value="pre">PRE</MenuItem>
            <MenuItem value="post">POST</MenuItem>
          </Select>
        </FormControl>
      </FormGroup>

      {/**
            <pre>{JSON.stringify(input, null, 2)}</pre>
            */}

      {error && (
        <Alert severity="error">
          <pre>{JSON.stringify(error)}</pre>
        </Alert>
      )}

      {data && (
        <Alert severity="success">
          <Link>
            Auto JSON generated (runtime:{' '}
            {data.generateJsonFromOncodnaFiles.runtimeSeconds} seconds)
          </Link>
        </Alert>
      )}

      <Button
        onClick={async () => {
          await createJsonReport({ variables: { input } });
          onChange && onChange();
        }}
        variant="outlined"
        disabled={loading}
        color="primary"
        startIcon={loading ? <Loading /> : <DownloadIcon />}
      >
        Create JSON Report
      </Button>
    </>
  );
};

const PublishPdfReportComponent = ({ ssid, onChange }) => {
  const [sendNotification, setSendNotification] = useState(false);
  const [publishPdfReport, { data, loading, error }] = useMutation(gql`
    mutation PublishPdfReportMutation(
      $input: PublishPdfReportMutationInputType!
    ) {
      publishPdfReport(input: $input) {
        id
        integrateIdentifier
      }
    }
  `);
  const input = {
    ssid,
    sendNotification,
  };

  return (
    <>
      <Alert severity="warning">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Typography variant="body1">Publish PDF report</Typography>

          <FormControlLabel
            label="Send notification"
            control={
              <Checkbox
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
              />
            }
          />

          <Button
            onClick={() => {
              publishPdfReport({ variables: { input } });
              onChange && onChange();
            }}
            variant="contained"
            disabled={loading}
            color="primary"
            startIcon={loading ? <Loading /> : <DownloadIcon />}
          >
            Publish PDF Report
          </Button>
        </div>
      </Alert>
    </>
  );
};

const CreatePdfReportComponent = ({ ssid, onChange }) => {
  const [isMab, setIsMab] = useState(false);
  const input = {
    ssid,
    type: isMab ? 'mab' : 'auto',
  };
  const [createJsonReport, { data, loading, error }] = useMutation(gql`
    mutation CreatePdfReportMutation(
      $input: GeneratePatientReportMutationInputType!
    ) {
      generatePatientReport(input: $input) {
        url
        runtimeSeconds
      }
    }
  `);
  return (
    <FormGroup>
      <FormControlLabel
        control={
          <Checkbox
            checked={isMab}
            onChange={(e) => setIsMab(e.target.checked)}
          />
        }
        label="MAB Report (include drug recomendations)"
      />
      {error && <Alert severity="error">{error.message}</Alert>}
      {data && (
        <Alert severity="success">
          <Link>
            PDF report generated (runtime:{' '}
            {data.generatePatientReport.runtimeSeconds} seconds)
          </Link>
        </Alert>
      )}
      <Button
        onClick={() => {
          createJsonReport({ variables: { input } });
          onChange && onChange();
        }}
        variant="outlined"
        disabled={loading}
        color="primary"
        startIcon={loading ? <Loading /> : <DownloadIcon />}
      >
        Create PDF Report
      </Button>
    </FormGroup>
  );
};

export const PatientReport = () => {
  const { me } = useMe();
  // const { ssid } = useParams()

  const [ssid, setSsid] = useState('2228');

  const history = useHistory();
  const { data, loading, error, refetch } = useQuery(
    gql`
      query GetPatientReport($ssid: String!) {
        patientReports(ssid: $ssid) {
          patient {
            id
          }
          reportAuto {
            pdfUrl
            jsonUrl
          }
          reportMab {
            pdfUrl
            jsonUrl
          }
        }
      }
    `,
    {
      variables: { ssid },
    },
  );
  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div
          style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 10 }}
        >
          <FormGroup>
            <MyTextField
              label="SSID"
              autoFocus
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => history.push(`/patient/${ssid}/summary`)}
                    >
                      <LaunchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormGroup>

          {error && <Alert severity="error">{error.message}</Alert>}

          {data && data.patientReports && (
            <>
              <div
                style={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <Card>
                  <CardContent>
                    <CreateJsonReportComponent ssid={ssid} onChange={refetch} />
                    {data.patientReports.reportAuto.jsonUrl && (
                      <Link
                        onClick={() => {
                          const url =
                            Settings.BACKEND_URL +
                            data.patientReports.reportAuto.jsonUrl;
                          window.open(url, '_blank');
                        }}
                      >
                        Download JSON report
                      </Link>
                    )}
                  </CardContent>
                </Card>

                {data.patientReports.reportAuto.jsonUrl && (
                  <Card>
                    <CardContent>
                      <CreatePdfReportComponent
                        ssid={ssid}
                        onChange={refetch}
                      />

                      <Stack
                        direction="column"
                        spacing={2}
                        style={{ marginTop: 10 }}
                      >
                        {data.patientReports.reportAuto.pdfUrl && (
                          <Link
                            onClick={() => {
                              const url =
                                Settings.BACKEND_URL +
                                data.patientReports.reportAuto.pdfUrl;
                              window.open(url, '_blank');
                            }}
                          >
                            Download Auto PDF report
                          </Link>
                        )}

                        {data.patientReports.reportMab.pdfUrl && (
                          <Link
                            onClick={() => {
                              const url =
                                Settings.BACKEND_URL +
                                data.patientReports.reportMab.pdfUrl;
                              window.open(url, '_blank');
                            }}
                          >
                            Download MAB PDF report
                          </Link>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {data.patientReports.reportAuto.pdfUrl && (
                  <>
                    <PublishPdfReportComponent
                      ssid={ssid}
                      onChange={async () => {
                        await refetch();
                        window.alert('PDF report published');
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
